/**
 * Header Bundle with LeftDrawer Functionality
 * 
 * Este arquivo contém:
 * - Store de modais integrado com funcionalidades do leftDrawer
 * - Componente Header com métodos para controlar o leftDrawer
 * - Componente PredictiveSearch
 * - Funções utilitárias necessárias
 * 
 * Funcionalidades do LeftDrawer:
 * - Abertura/fechamento via métodos do Header
 * - Controle via tecla Escape
 * - Eventos customizados para integração com outros componentes
 * - Suporte a transições e animações
 */

// Definir variáveis globais necessárias para templates Liquid
if (typeof window !== 'undefined') {
  // Variável global para current_media_id usada em templates de produto
  window.current_media_id = null;
}

// Utility functions
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Note: Modals store is now defined in modals.js to avoid conflicts

// Header component
var HeaderComponent = (count) => {
  const dropdownCount = parseInt(count);
  
    return {
      menuOpen: {},
      searchOpen: false,
      headerIsSticky: false,
      scrollY: 0,
      navWrapped: false,
      headerHeight: 0,
      activeWrappedMenuToggleEl: null,
    
      get isStuck() {
        return this.headerIsSticky && this.scrollY > this.headerHeight * 2;
      },
    
      get menuIsActive() {
        return !Object.keys(this.menuOpen).every((k) => !this.menuOpen[k]);
      },
    
      get headerIsActive() {
        return this.menuIsActive || this.searchOpen;
      },
    
      init() {
      // Register modal for mobile navigation
      if (window.Alpine && window.Alpine.store('modals')) {
        window.Alpine.store('modals').register('nav', 'leftDrawer');
      }
      
      // Initialize menu states
      for (let i = 0; i < dropdownCount; i++) {
          this.menuOpen["menu" + i] = false;
        }
      
      // Add event listeners
        document.addEventListener("keyup", this.keyUpListener.bind(this));
        document.body.addEventListener(
          "shapes:search:closebutton",
          this.searchCloseButtonListener.bind(this)
        );
      
      // Setup sticky header if needed
        if (this.$root.hasAttribute("data-sticky-header")) {
          this.setUpStickyDetection();
        }
      
      // Setup header resize functions
        this.headerResizeFunctions();
        this._debouncedHeaderFunctions = debounce(
          this.headerResizeFunctions.bind(this),
          300
        );
      
        window.addEventListener("resize", this._debouncedHeaderFunctions);
      
      // Shopify design mode support
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.sectionLoadListener.bind(this)
          );
        }
      
      // Watch for changes
        this.$watch("menuIsActive", (value) => {
          if (!value && this.activeWrappedMenuToggleEl) {
            this.activeWrappedMenuToggleEl = null;
          }
        });
      
        this.$watch("navWrapped", (value) => {
          if (value) {
            this.activeWrappedMenuToggleEl = null;
          }
        });
      },
    
      headerResizeFunctions() {
        this.calculateHeaderHeight();
        this.calculateNavWrap();
      },
    
      setUpStickyDetection() {
        const headerContainerEl = this.$root.closest(".shopify-section");
        const sentinelEl = document.createElement("div");
        sentinelEl.setAttribute("id", "HeaderSentinel");
        headerContainerEl.parentNode.insertBefore(sentinelEl, headerContainerEl);
      
        const observer = new IntersectionObserver(
          (e) => {
            e.forEach((entry) => {
              this.headerIsSticky = entry.intersectionRatio < 1 ? true : false;
            });
          },
          {
            rootMargin: "-1px 0px 0px 0px",
            threshold: [1]
          }
        );
      
        observer.observe(sentinelEl);
      
        let timer = null;
        const checkScrollPosition = (e) => {
          this.scrollY = window.scrollY;
          if (timer !== null) {
            clearTimeout(timer);
          }
          timer = setTimeout(() => {
            this.scrollY = window.scrollY;
          }, 150);
        };
      
        this._throttledScrollHandler = throttle(checkScrollPosition, 150);
        window.addEventListener(
          "scroll",
          this._throttledScrollHandler.bind(this),
          {
            passive: true
          }
        );
      
        this.scrollY = window.scrollY;
      },
    
      calculateNavWrap() {
        const wrappedItems = [];
        let prevItemRect = {};
        let currItemRect = {};
      
        if (this.$refs.navigation !== void 0) {
          const items = this.$refs.navigation.children;
        
          for (let i = 0; i < items.length; i++) {
            const currItem = items[i];
          
            window.requestAnimationFrame(() => {
              currItemRect = currItem.getBoundingClientRect();
            
              if (prevItemRect && prevItemRect.top < currItemRect.top) {
                wrappedItems.push(items[i]);
              }
            
              prevItemRect = currItemRect;
            
              if (i === items.length - 1) {
                if (wrappedItems.length > 0) {
                  this.navWrapped = true;
                } else {
                  this.navWrapped = false;
                }
              }
            });
          }
        }
      },
    
      calculateHeaderHeight() {
        window.requestAnimationFrame(() => {
          this.headerHeight = document.getElementById("headerBorderWrap").clientHeight;
          document.documentElement.style.setProperty(
            "--header-height",
            `${this.headerHeight}px`
          );
        });
      },
    
      toggleMenu(index) {
        this.menuOpen["menu" + index] = !this.menuOpen["menu" + index];
      },
    
      focusOut(event, menu) {
        if (event.relatedTarget) {
          let focusedOnTopLevelButton = false;
          let dropdownParent = event.relatedTarget.getAttribute(
            "data-dropdown-parent"
          );
        
          if (dropdownParent && dropdownParent === menu) {
            focusedOnTopLevelButton = true;
          }
        
          const focusedOnChild = event.relatedTarget.closest(
            "[data-header-dropdown]"
          );
        
          if (!focusedOnTopLevelButton && !focusedOnChild) {
            this.menuOpen[menu] = false;
          }
        }
      },
    
      searchFocusOut() {
        this.$nextTick(() => {
          if (!this.$refs.searchFieldComponent.contains(document.activeElement))
            this.searchOpen = false;
        });
      },
    
      closeSearch() {
        this.searchOpen = false;
        this.$refs.search.focus();
      },
    
      openSearch() {
        this.searchOpen = true;
        let input = document.querySelector(".header-search-input");
        setTimeout(() => {
          input.focus();
        }, 100);
      },
    
      // LeftDrawer specific methods
      openLeftDrawer(content = 'nav') {
        if (window.Alpine && window.Alpine.store('modals')) {
          window.Alpine.store('modals').open(content);
        }
      },
    
      closeLeftDrawer() {
        if (window.Alpine && window.Alpine.store('modals')) {
          window.Alpine.store('modals').close('leftDrawer');
        }
      },
    
      toggleLeftDrawer(content = 'nav') {
        if (window.Alpine && window.Alpine.store('modals')) {
          const store = window.Alpine.store('modals');
          if (store.isOpen(content)) {
            this.closeLeftDrawer();
          } else {
            this.openLeftDrawer(content);
          }
        }
      },
    
      isLeftDrawerOpen(content = 'nav') {
        if (window.Alpine && window.Alpine.store('modals')) {
          return window.Alpine.store('modals').isOpen(content);
        }
        return false;
      },
    
      keyUpListener(event) {
        if (event.key === "Escape") {
          // Close leftDrawer if open
          if (window.Alpine && window.Alpine.store('modals') && window.Alpine.store('modals').isOpen('nav')) {
            this.closeLeftDrawer();
            return;
          }
          
          if (this.searchOpen === false) {
            for (let i = 0; i < dropdownCount; i++) {
              if (this.menuOpen["menu" + i] === true) {
                this.menuOpen["menu" + i] = false;
                document.querySelector(`[aria-controls="menu${i}"]`).focus();
              }
            }
          } else {
            if (!document.getElementById("predictiveSearchResults")) {
              document.body.dispatchEvent(
                new CustomEvent("shapes:search:closebutton")
              );
            }
          }
        }
      },
    
      searchCloseButtonListener() {
        this.searchOpen = false;
        this.$nextTick(() => {
          const searchToggleEl = this.$root.querySelector("[data-open-search]");
          if (searchToggleEl) {
            setTimeout(() => {
              searchToggleEl.focus();
            }, 300);
          }
        });
      },
    
      sectionLoadListener(event) {
        if (!event.target.querySelector(".site-header")) return;
        this.headerResizeFunctions();
      },
    
      destroy() {
        document.removeEventListener("keyup", this.keyUpListener);
        document.body.removeEventListener(
          "shapes:search:closebutton",
          this.searchCloseButtonListener
        );
        window.removeEventListener("resize", this._debouncedHeaderFunctions);
      
      if (this._throttledScrollHandler) {
        window.removeEventListener("scroll", this._throttledScrollHandler);
        }
      }
    };
  };
  
// Predictive Search component
var PredictiveSearchComponent = (resources) => ({
    cachedResults: {},
    loading: false,
    resultsOpen: false,
    rawQuery: "",
    results: false,
    resultsMarkup: null,
    resources,
  
    get trimmedQuery() {
      return this.rawQuery.trim();
    },
  
    get queryKey() {
      return this.trimmedQuery.replace(" ", "-").toLowerCase();
    },
  
    init() {
      console.log('PredictiveSearch component initialized with resources:', this.resources);
      this.cachedResults = {};
      const toggles = document.querySelectorAll("[data-open-search]");
      toggles.forEach((toggle) => {
        toggle.setAttribute("role", "button");
      });
    
      this.$watch("searchOpen", (value) => {
        if (value === true) {
          this.onOpen();
        }
      });
    },
  
    close(clearSearchTerm = true) {
      this.closeResults(clearSearchTerm);
      this.closePredictiveSearch();
    },
  
    closePredictiveSearch() {
      document.body.dispatchEvent(new CustomEvent("shapes:search:closebutton"));
    },
  
    closeResults(clearSearchTerm = false) {
      this.resultsOpen = false;
      if (clearSearchTerm) {
        this.rawQuery = "";
      }
    
      const selected = this.$root.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute("aria-selected", false);
    
      this.$refs.input.setAttribute("aria-activedescendant", "");
      this.$refs.input.setAttribute("aria-expanded", false);
      document.documentElement.style.overflowY = "auto";
    },
  
    getSearchResults() {
      this.loading = true;
      liveRegion(window.theme.strings.loading);
      
      const searchUrl = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(this.trimmedQuery)}&${encodeURIComponent('resources[type]')}=${this.resources}&section_id=predictive-search`;
      console.log('Search URL:', searchUrl);
      
      fetch(searchUrl)
        .then(response => {
          this.loading = false;
          if (!response.ok) {
            var error = new Error(response.status);
            console.error('Search API error:', error);
            this.close(true);
            throw error;
          }
          return response.text();
        })
        .then(text => {
          this.results = true;
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          const liveRegionText = new DOMParser().parseFromString(text, 'text/html').querySelector('#predictive-search-count').textContent;
          this.cachedResults[this.queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
          liveRegion(liveRegionText);
        })
        .catch(error => {
          console.error('Search error:', error);
          this.close(true);
          throw error;
        });
    },
  
    onChange() {
      if (!this.trimmedQuery.length) {
        this.closeResults();
      } else {
        this.openResults();
        this.getSearchResults();
      }
    },
  
    onFocus() {
      if (!this.trimmedQuery.length) return;
      if (this.results === true) {
        this.openResults();
      } else {
        this.getSearchResults();
      }
    },
  
    onFormSubmit(event) {
      // Only prevent default if a suggestion is selected (let user click on it)
      if (this.$root.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
      // If no query, let the form submit normally to show "no results" page
    },
  
    onKeyup() {
      this.$event.preventDefault();
      switch (this.$event.code) {
        case "ArrowUp":
          this.switchOption("up");
          break;
        case "ArrowDown":
          this.switchOption("down");
          break;
        case "Enter":
          this.selectOption();
          break;
      }
    },
  
    onKeydown() {
      if (this.$event.code === "Escape" && this.trimmedQuery.length) {
        this.$event.preventDefault();
      }
    if (this.$event.code === "ArrowUp" || this.$event.code === "ArrowDown" || this.$event.code === "Enter") {
        this.$event.preventDefault();
      }
    },
  
    onOpen() {
      if (this.trimmedQuery.length) {
        this.openResults();
        this.$nextTick(() => {
          if (this.cachedResults[this.queryKey]) {
            this.renderSearchResults(this.cachedResults[this.queryKey]);
          } else {
            this.getSearchResults();
          }
        });
      }
    },
  
    openResults() {
      this.resultsOpen = true;
      this.$refs.input.setAttribute("aria-expanded", true);
      document.documentElement.style.overflowY = "hidden";
    },
  
    renderSearchResults(resultsMarkup) {
      if (this.$refs.results) {
        this.$refs.results.innerHTML = resultsMarkup;
        this.results = true;
        this.openResults();
        setTimeout(() => {
          this.$refs.input.focus();
        }, 300);
      }
    },
  
    selectOption() {
      const selectedProduct = this.$root.querySelector(
        '[aria-selected="true"] a, [aria-selected="true"] button'
      );
      if (selectedProduct) selectedProduct.click();
    },
  
    switchOption(direction) {
      if (!this.resultsOpen) return;
    
      const moveUp = direction === "up";
      const selectedElement = this.$root.querySelector('[aria-selected="true"]');
      const allElements = Array.from(
        this.$root.querySelectorAll(".predictive-search__option")
      );
    
      let activeElement = this.$root.querySelector(".predictive-search__option");
    
      if (moveUp && !allElements.length) return;
    
      if (!moveUp && selectedElement) {
        activeElement = allElements[allElements.indexOf(selectedElement) + 1] || allElements[0];
      } else if (moveUp) {
        activeElement = allElements[allElements.indexOf(selectedElement) - 1] || allElements[allElements.length - 1];
      }
    
      if (activeElement === selectedElement) return;
    
      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) selectedElement.setAttribute("aria-selected", false);
    
      this.$refs.input.setAttribute("aria-activedescendant", activeElement.id);
      activeElement.scrollIntoView(false, { behavior: "smooth" });
    }
  });
  
// Initialize when Alpine.js is available
if (typeof window !== 'undefined') {
  console.log("Header bundle script loaded, checking Alpine.js availability...");
  
  // Function to register our components
  const registerComponents = () => {
    try {
      // Register Header component
      console.log("Registering Header component...");
      Alpine.data("Header", HeaderComponent);
      console.log("Header component registered successfully");
      
      // Register PredictiveSearch component
      console.log("Registering PredictiveSearch component...");
      Alpine.data("PredictiveSearch", PredictiveSearchComponent);
      console.log("PredictiveSearch component registered successfully");
      
      // Make components available globally
      window.header_default = HeaderComponent;
      console.log("Components made available globally");
      
      console.log("Header bundle initialized successfully");
      
      // Verify the components are registered
      console.log("Available Alpine components:", Object.keys(Alpine.data));
      console.log("Available Alpine stores:", Object.keys(Alpine.store));
      
      return true;
    } catch (error) {
      console.error("Error during Alpine initialization:", error);
      console.error("Error stack:", error.stack);
      return false;
    }
  };
  
  // Wait for DOM to be ready and Alpine to be available
  const initAlpine = () => {
    console.log("initAlpine called, checking if Alpine is available...");
    console.log("window.Alpine:", window.Alpine);
    
    if (window.Alpine) {
      console.log("Alpine.js is available, initializing components...");
      
      if (registerComponents()) {
        console.log("All components registered successfully");
      } else {
        console.log("Failed to register components, retrying...");
        setTimeout(initAlpine, 100);
      }
    } else {
      console.log("Alpine.js not available yet, waiting...");
      // Alpine not ready yet, wait a bit more
      setTimeout(initAlpine, 100);
    }
  };
  
  // Try to initialize immediately if Alpine is already available
  if (document.readyState === 'loading') {
    console.log("Document still loading, waiting for DOMContentLoaded...");
    // Document still loading, wait for DOMContentLoaded
    document.addEventListener('DOMContentLoaded', initAlpine);
  } else {
    console.log("Document already loaded, trying to initialize...");
    // Document already loaded, try to initialize
    initAlpine();
  }
  
  // Also try to register when Alpine is initialized (backup method)
  document.addEventListener('alpine:init', () => {
    console.log("Alpine init event detected, registering components...");
    if (window.Alpine) {
      registerComponents();
    }
  });
}

/**
 * EXEMPLO DE USO DO LEFTDRAWER NO HTML:
 * 
 * 1. No template Liquid, adicione o elemento do leftDrawer:
 * 
 * <div id="left-drawer-slot" x-show="$store.modals.leftDrawer.open" 
 *      x-transition:enter="transition ease-out duration-300"
 *      x-transition:enter-start="-translate-x-full"
 *      x-transition:enter-end="translate-x-0"
 *      x-transition:leave="transition ease-in duration-200"
 *      x-transition:leave-start="translate-x-0"
 *      x-transition:leave-end="-translate-x-full">
 *   <!-- Conteúdo do drawer -->
 * </div>
 * 
 * 2. Para abrir o leftDrawer, use no componente Header:
 * 
 * <button @click="openLeftDrawer('nav')">Abrir Menu</button>
 * <button @click="toggleLeftDrawer('nav')">Toggle Menu</button>
 * <button @click="closeLeftDrawer()">Fechar Menu</button>
 * 
 * 3. Para verificar se está aberto:
 * 
 * <div x-show="isLeftDrawerOpen('nav')">Menu está aberto</div>
 * 
 * 4. Eventos disponíveis:
 * - nav-will-open
 * - nav-is-open
 * - left-drawer-will-open
 * - left-drawer-is-open
 * - nav-will-close
 * - nav-is-closed
 * - left-drawer-will-close
 * - left-drawer-is-closed
 * 
 * 5. Acessar o store diretamente:
 * 
 * Alpine.store('modals').open('nav')
 * Alpine.store('modals').close('leftDrawer')
 * Alpine.store('modals').isOpen('nav')
 */
  